import React, { useEffect, useState } from "react";
import ConditionEditor from "./ConditionEditor";
import FormInput from "./FormInput";
import OptionsEditor from "./OptionsEditor";
import {
  DynamicSourceType,
  FormDefinition,
  KNOWN_DYNAMIC_SOURCES,
  OptionContentType,
  QUESTION_TYPES,
  QuestionnaireItem as Question,
} from "../../../../interfaces/dynamicForms/FormBuilder";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import { Button } from "react-bootstrap";
import Form from 'react-bootstrap/Form';


interface QuestionEditorProps {
  question: Question;
  stepId: string;
  formDefinition: FormDefinition;
  updateQuestion: (
    stepId: string,
    questionId: string,
    updatedQuestion: Question
  ) => void;
  deleteQuestion: (stepId: string, questionId: string) => void;
  isFirst: boolean;
  order: number;
  isLast: boolean;
  reorderQuestion: (
    stepId: string,
    questionId: string,
    direction: "up" | "down"
  ) => void;
  moveQuestionAcrossSteps: (
    fromStepId: string,
    questionId: string,
    toStepId: string
  ) => void;
  stepIndex: number;
  totalSteps: number;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({
  question,
  stepId,
  formDefinition,
  updateQuestion,
  deleteQuestion,
  isFirst,
  isLast,
  reorderQuestion,
  order,
  moveQuestionAcrossSteps,
  stepIndex,
  totalSteps,
}) => {
  const [localId, setLocalId] = useState(question.id);

  useEffect(() => {
    setLocalId(question.id);
  }, [question.id]);

  const handleChange = (field: keyof Question, value: any) => {
    if (field === "id") {
      return;
    }
    let updatedQuestion = { ...question, [field]: value };
    updateQuestion(stepId, question.id, updatedQuestion);
  };
  const handleValidationChange = (
    field: keyof NonNullable<Question["validation"]>,
    value: any
  ) => {
    const currentValidation = question.validation || {};
    handleChange("validation", { ...currentValidation, [field]: value });
  };

  const handleShowIfUpdate = (newConditions: Question["showIf"]) => {
    handleChange("showIf", newConditions);
  };
  const handleHideIfUpdate = (newConditions: Question["hideIf"]) => {
    handleChange("hideIf", newConditions);
  };
  const handleOptionsUpdate = (newOptions: OptionContentType[]) => {
    handleChange("options", newOptions);
  };

  const handleTransformChange = (field: 'pattern' | 'replacement', value: string) => {
    const currentTransform = (question as any).optionLabelTransform || {};
    const newTransform = { ...currentTransform, [field]: value };
    handleChange('optionLabelTransform' as any, newTransform);
  };

  const isReactSelect = question.type === "react-select";
  const isRadio = question.type === "radio";
  const isNumber = question.type === "number";
  const isCurrency = question.type === "currency";
  const isTextBased = ["text", "paragraph", "tel"].includes(question.type);
  const usesPlaceholder = [
    "text",
    "paragraph",
    "tel",
    "react-select",
    "number",
    "currency",
  ].includes(question.type);
  const requiresOptions = isReactSelect || isRadio;
  const isDynamic = isReactSelect && !!question.dynamicSource;

  return (
    <>
      <div className="common_card mby_20">
        <div className="top top_bar_flx border_tittle mby_16">
          <div className="top_bar_tittle sm_tittle">
            <h2>Question {order + 1}: {question.id}</h2>
          </div>
          <div className="top_right_flx">
            <button
              onClick={() => reorderQuestion(stepId, question.id, "up")}
              disabled={isFirst}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
              title="Move this question up in the same step"
              aria-label="Move Up"
            >
              <FontAwesomeIcon icon={faArrowUp} />
            </button>
            <button
              onClick={() => reorderQuestion(stepId, question.id, "down")}
              disabled={isLast}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
              title="Move this question down in the same step"
              aria-label="Move Down"
            >
              <FontAwesomeIcon icon={faArrowDown} />
            </button>
            <div className="flex items-center space-x-1">
              <button
                onClick={() =>
                  moveQuestionAcrossSteps(
                    stepId,
                    question.id,
                    formDefinition.steps[stepIndex - 1]?.id
                  )
                }
                disabled={stepIndex === 0}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                title="Move this question to the previous step"
                aria-label="Move to previous step"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <button
                onClick={() =>
                  moveQuestionAcrossSteps(
                    stepId,
                    question.id,
                    formDefinition.steps[stepIndex + 1]?.id
                  )
                }
                disabled={stepIndex === totalSteps - 1}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                title="Move this question to the next step"
                aria-label="Move to next step"
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
              <Button variant="danger" className="btn_sm" onClick={() => deleteQuestion(stepId, question.id)}>Delete Question</Button>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 labelBlock">
            <div className="custom_input_flled">
              <FormInput
                label="Question ID"
                id={`q-${question.id}-id`}
                value={localId}
                onChange={(e) => setLocalId(e.target.value)}
                onBlur={() =>
                  updateQuestion(stepId, question.id, { ...question, id: localId })
                }
                required
              />{" "}
              <div className="stats_filled mt-1">
                <p>
                  The unique ID for this question. The user's answer is saved using
                  this ID, so it <strong>should not be changed</strong> once the form
                  is in use.
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-6 labelBlock">
            <div className="custom_input_flled">
              <FormInput
              style={{maxWidth: 'unset'}}
                className="sm_input filled_white "
                label="Label / Text"
                id={`q-${question.id}-label`}
                value={question.label}
                onChange={(e) => handleChange("label", e.target.value)}
                required
              />
              <div className="stats_filled mt-1">
                <p>
                  The text of the question that the user will see (e.g., 'What is your
                  date of birth?').
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-6 labelBlock">
            <div className="custom_input_flled">
              <label
                htmlFor={`q-${question.id}-type`}
              
              >
                Type
              </label>
              <select
              style={{maxWidth: 'unset'}}
                className="sm_input filled_white "
                id={`q-${question.id}-type`}
                value={question.type}
                onChange={(e) =>
                  handleChange("type", e.target.value as Question["type"])
                }
              
              >
                {QUESTION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <div className="stats_filled mt-1">
                <p>
                  Controls the input the user sees. For example, `text` is a simple
                  box, `react-select` provides a searchable dropdown menu, and `date`
                  shows a calendar.
                </p>
              </div>
            </div>
          </div>
          {(question.type === "text" ||
            question.type === "tel" ||
            question.type === "currency") && (
              <div className="col-md-6 labelBlock">
                <div className="custom_input_flled">
                  <FormInput
                  style={{maxWidth: 'unset'}}
                  className="sm_input filled_white "
                    label="Prefix"
                    id={`q-${question.id}-prefix`}
                    value={question.prefix || ""}
                    onChange={(e) => handleChange("prefix", e.target.value)}
                  />
                  <div className="stats_filled mt-1">
                    <p>
                      A value automatically added to the beginning of the user's answer
                      when it is saved.
                    </p>
                  </div>
                </div>
            </div>
          )}
          {/* ... Other conditional fields like Placeholder, MaxLength, etc. with their own descriptions ... */}
        </div>

        {requiresOptions && (
          <>
            <hr />
            {isReactSelect && (
              <>
                <div className="mby_20">
                  <Form.Check
                    style={{ padding: "0", marginLeft: "1.4375rem" }}
                    label="Allow multiple selections?"
                    type="checkbox"
                    checked={question.isMulti || false}
                    onChange={(e) =>
                      handleChange("isMulti", e.target.checked)
                    }
                  />
                  <div className="stats_filled mt-1" style={{ marginLeft: "1.4375rem" }}>
                    <p>
                      If checked, the user can select multiple options from the dropdown.
                    </p>
                  </div>
                </div>
                <div className="custom_input_flled">
                  <label htmlFor={`q-${question.id}-dynamicSource`}>Dynamic Options Source</label>
                  <select
                    id={`q-${question.id}-dynamicSource`}
                    value={question.dynamicSource || ""}
                    onChange={(e) => handleChange("dynamicSource", e.target.value || undefined)}
                    className="sm_input filled_white"
                    style={{ maxWidth: 'unset' }}
                  >
                    <option value="">None (Use Manual Options)</option>
                    {KNOWN_DYNAMIC_SOURCES.map(source => (
                      <option key={source} value={source}>{source}</option>
                    ))}
                  </select>
                  <div className="stats_filled mt-1">
                    <p>
                      Automatically populate options from a system data source, like a list of all participants. If selected, manual options below will be disabled.
                    </p>
                  </div>
                </div>
                {isDynamic && (
                  <div className="common_card" style={{backgroundColor: 'var(--main-content-bg)'}}>
                      <div className="top border_tittle">
                          <div className="top_bar_tittle flex-column align-items-start">
                              <h2>Dynamic Option Label Transformation</h2>
                              <p>Use this to reformat the labels of options loaded from the dynamic source. For example, you can remove a prefix like "OAAS-".</p>
                          </div>
                      </div>
                      <div className="row">
                          <div className="col-md-6">
                              <FormInput
                                  label="Pattern to Find (Regex)"
                                  id={`q-${question.id}-transform-pattern`}
                                  value={(question as any).optionLabelTransform?.pattern || ''}
                                  onChange={(e) => handleTransformChange('pattern', e.target.value)}
                                  placeholder="e.g., OAAS-"
                              />
                          </div>
                          <div className="col-md-6">
                              <FormInput
                                  label="Replacement Text"
                                  id={`q-${question.id}-transform-replacement`}
                                  value={(question as any).optionLabelTransform?.replacement || ''}
                                  onChange={(e) => handleTransformChange('replacement', e.target.value)}
                                  placeholder="e.g., (leave empty to remove)"
                              />
                          </div>
                      </div>
                  </div>
                )}
              </>
            )}
            {/* ... OptionsEditor and its related controls ... */}
            <OptionsEditor
              options={question.options || []}
              updateOptions={handleOptionsUpdate}
              disabled={isDynamic}
            />
          </>
        )}
        <hr />
        <div className="">
          <div className="top mby_16">
            <div className="top_bar_tittle">
              <h2>Validation</h2>
            </div>
          </div>
          <Form.Check
            style={{padding: '0', marginLeft: '1.4375rem'}}
            label ='Required?'
            type="checkbox"
            checked={question.validation?.required || false}
            onChange={(e) =>
              handleValidationChange("required", e.target.checked)
            }
          />
          <div className="stats_filled mt-1">
            <p>
              If checked, this question <strong>must</strong> be answered. For more
              complex logic, use the <strong>Rules Editor</strong>.
            </p>
          </div>
        </div>
        <hr />
        <div className="stats mby_20">
          <h3>Conditional Display Logic</h3>
          <p>Create rules to dynamically show or hide this question based on answers to other questions.</p>
        </div>
        <div className="mby_20">
          <ConditionEditor
            conditions={question.showIf || []}
            formDefinition={formDefinition}
            updateConditions={handleShowIfUpdate}
            title="Show This Question If..."
          />
        </div>
        <div className="">
          <ConditionEditor
            conditions={question.hideIf || []}
            formDefinition={formDefinition}
            updateConditions={handleHideIfUpdate}
            title="Hide This Question If..."
          />
        </div>
      </div>
    </>
  );
};

export default QuestionEditor;
